import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Box,
  Flex,
  Text,
  Textarea,
  Heading,
  VStack,
  HStack,
  IconButton,
  FormControl,
  FormErrorMessage,
  Collapse,
  useToast,
  Container,
  useBreakpointValue,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { TbAlertCircle } from 'react-icons/tb';
import { FaRocketchat } from 'react-icons/fa';
import PromptCard from './components/PromptCard';
import ChatBox from 'components/ChatBox';
import theme from 'ui/theme/theme';
import { Wrapper } from './style/WritingPracticePage.styles';
import { instr_brainstorm } from 'utils/data/constants';
import InactiveBanner from './components/InactiveBanner';
import SmallScreenAlert from './components/SmallScreenAlert';
import { essay_schema } from './data/essay_schema';

const QUERY_INTERVAL = 20;

function BrainstormForm({
  breadcrumbProg,
  setBreadcrumbProg,
  nextPath,
  isPlanActive,
}) {
  // console.log('11111111111111111111 -- entering brain storm page: ');
  const navigate = useNavigate();
  const { essayId } = useParams();
  const toast = useToast();
  const isNarrow = useBreakpointValue({ base: true, md: true, lg: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [exerciseData, setExerciseData] = useState({});
  const [currIdeas, setCurrIdeas] = useState('');
  const [brainstorms, setBrainstorms] = useState({});
  const [promptData, setPromptData] = useState({});
  const [enableNext, setEnableNext] = useState(false);
  const [lastQueryTime, setLastQueryTime] = useState(
    Math.round(Date.now() / 1000 - QUERY_INTERVAL)
  );

  const [isChatOpen, setIsChatOpen] = useState(true);

  const essayDetails = {
    ...essay_schema,
    ...JSON.parse(localStorage.getItem('essayExerciseDetails')),
  };

  function updateStateData(details) {
    setExerciseData(details);
    setBrainstorms(details.brainstorms);
    setCurrIdeas(details.brainstorms?.ideas);
    setPromptData({
      essayTitle: details.essayTitle,
      essayType: details.secondaryType || details.primaryType,
      essayPrompt: details.prompt,
      url: details.illustration,
      level: details.level,
      minWordCount: details.minWordCount,
    });
    setEnableNext(details.progress > 1 ? true : false);
  }

  useEffect(() => {
    setBreadcrumbProg(breadcrumbProg);
  }, [setBreadcrumbProg, breadcrumbProg]);

  useEffect(() => {
    if (essayDetails?.essayId == essayId) {
      updateStateData(essayDetails);
    }
    if (
      essayDetails?.essayId != essayId ||
      (essayDetails.progress > 1 && !currIdeas)
    ) {
      api
        .get(`${API_BASE}/api/essay-exercise/retrieve-data?exerciseId=${essayId}`)
        .then((res) => {
          // console.log('retrieve data from brainstorm page, res: =====', res);
          if (res.status == 200) {
            if (res.data?.code == 200) {
              const newExerciseData = res.data?.data;
              updateStateData(newExerciseData);
              localStorage.setItem(
                'essayExerciseDetails',
                JSON.stringify(newExerciseData)
              );
            }
          }
        })
        .catch(() =>
          console.log('failed to fetch essay exercise, id: ', essayId)
        );
    }
  }, []);

  useEffect(() => {
    api
      .get(`${API_BASE}/api/chat/exercise-chats?exerciseId=${essayId}&section=1`)
      .then((res) => {
        if (res.status == 200) {
          setMessages(res.data?.data);
        }
      });
  }, [essayId]);

  const validationSchema = Yup.object({
    brainstorm: Yup.string()
      .min(20, 'Must be at least 20 characters')
      .required('This field is required'),
  });

  function updateBrainstorms(values, finished) {
    setIsSubmitting(true);
    // console.log('==============to update brainstorm ideas: ', values);
    api
      .patch(`${API_BASE}/api/essay-exercise/brainstorms`, {
        exerciseId: essayId,
        ideas: values?.brainstorm,
        finished: finished,
      })
      .then((res) => {
        // console.log('=======patch brainstorms response: ', res);
        if (res.status == 200) {
          const essayDetails = res.data?.data;
          localStorage.setItem(
            'essayExerciseDetails',
            JSON.stringify(essayDetails)
          );
          if (finished) {
            navigate(nextPath);
          }
        }
      })
      .catch((err) => console.log('failed to update ideas, reason: ', err))
      .finally(() => setIsSubmitting(false));
  }

  const assistantChat = (answer, type) => {
    const body = {
      brainstorm: answer,
      exerciseId: essayId,
      chatType: type,
      writingPrompt: exerciseData?.prompt,
      essayType: exerciseData.secondaryType || exerciseData.primaryType,
      grade: exerciseData?.level,
    };

    const currTimeSec = Date.now() / 1000;
    let warning = '';
    if (currTimeSec - lastQueryTime < QUERY_INTERVAL) {
      warning =
        'Take a moment to review your work before submitting. We believe in your best effort!';
    }
    if ((!answer || answer.length < 20) && type === 3) {
      warning =
        'Your submission is a bit brief. We know you have great ideas to share!';
    }
    if (warning) {
      toastMessage(warning);
      return;
    }
    setIsChatOpen(true);
    setLastQueryTime(Date.now() / 1000);

    setIsSubmitting(true);
    api
      .post(`${API_BASE}/api/chat/brainstorm-assistant`, body)
      .then((res) => {
        // console.log('=======brainstorm assistant res: ', res);
        if (res.status == 200) {
          updateChatMessage(res.data?.data);
        }
      })
      .catch(() => toastMessage('Sorry, Ivy is busy.'))
      .finally(() => setIsSubmitting(false));
  };

  const toggleChatbox = () => setIsChatOpen((prev) => !prev);

  function updateChatMessage(chats) {
    let newMessages = [];
    messages.forEach((msg) => newMessages.push(msg));
    chats.forEach((c) => newMessages.push(c));
    setMessages(newMessages);
  }

  function toastMessage(msg) {
    toast({
      position: 'top',
      render: () => (
        <Box
          border="1px solid"
          // color={theme.colors.semantics['warning-light']}
          borderColor={theme.colors.semantics.warning}
          p={4}
          bg={theme.colors.semantics['warning-light']}
          borderRadius={12}
          wordBreak="break-all"
        >
          <Flex>
            <TbAlertCircle color={theme.colors.semantics.warning} />
            <Flex ml="12px" flex="1">
              <Text fontWeight="bold" ml="2">
                {msg}
              </Text>
            </Flex>
          </Flex>
        </Box>
      ),
      duration: 3000,
    });
  }

  if (isNarrow) {
    return <SmallScreenAlert />;
  }

  return (
    <Container width={isChatOpen ? '95%' : '75%'} maxW="none" minW="768px">
      {/* <HStack align="stretch" spacing={0}> */}
      {!isPlanActive && <InactiveBanner />}
      <Flex height="calc(100vh - 48px)" overflow="hidden">
        <Box
          flex="2" // 70% of the width
          overflowY="auto" // Independent scroll for this section
          p={4}
          minW="450px"
        // borderRight="1px solid #e0e0e0"
        // bg="gray.50"
        >
          <Flex flexDirection="column" marginBottom="16px">
            <PromptCard promptData={promptData} defaultShow={true} />
            <Heading size="md" color="teal.700">
              Brainstorming
            </Heading>
            {enableNext ? (
              <>
                <Box
                  border={`1px solid ${theme.colors.gray['400']}`}
                  bg="#F0FFF4"
                  borderRadius="8px"
                  p="16px 24px 16px 16px"
                  mt="16px"
                  mb="32px"
                  dangerouslySetInnerHTML={{ __html: currIdeas }}
                />
                <HStack justify="flex-end">
                  <Button
                    colorScheme="green"
                    type="button"
                    onClick={() => navigate(nextPath)}
                  >
                    Next
                  </Button>
                </HStack>
              </>
            ) : (
              <Formik
                initialValues={{ brainstorm: brainstorms?.ideas }}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={() => {
                  // console.log('Form submitted:', values);
                  // setSubmitting(false); // Stop submission indicator
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  // handleBlur,
                  // isSubmitting,
                }) => (
                  <Form>
                    <VStack spacing={4} mt={4} align="stretch">
                      <FormControl
                        isInvalid={errors.brainstorm && touched.brainstorm}
                      >
                        {/* Textarea Field */}
                        <Textarea
                          name="brainstorm"
                          placeholder="Brainstorm here..."
                          size="lg"
                          // resize="none"
                          rows={15} // Default 15 lines
                          minHeight="15em"
                          value={values.brainstorm}
                          onChange={(e) => {
                            handleChange(e);
                            setCurrIdeas(e.target.value);
                          }}
                        // onBlur={handleBlur} // Marks the field as touched
                        />
                        <FormErrorMessage>
                          <ErrorMessage name="brainstorm" />
                        </FormErrorMessage>
                      </FormControl>

                      <Box mt={1}>
                        <Flex align="center" justify="space-between" w="100%">
                          <Flex gap={2}>
                            <Button
                              size="sm"
                              isDisabled={!!errors.brainstorm || !isPlanActive}
                              isLoading={isSubmitting}
                              onClick={() =>
                                assistantChat(values?.brainstorm, 2)
                              }
                            >
                              I&apos;m stuck
                            </Button>
                            <Button
                              size="sm"
                              isDisabled={!!errors.brainstorm || !isPlanActive}
                              isLoading={isSubmitting}
                              onClick={() =>
                                assistantChat(values?.brainstorm, 3)
                              }
                            >
                              Evaluate
                            </Button>
                          </Flex>

                          {/* Buttons aligned horizontally */}
                          <Flex gap={2} mr={4}>
                            <Button
                              colorScheme="green"
                              type="button"
                              variant="outline"
                              isLoading={isSubmitting}
                              onClick={() => updateBrainstorms(values, false)}
                              isDisabled={!!errors.brainstorm}
                            >
                              Save for later
                            </Button>
                            <Button
                              colorScheme="green"
                              type="button"
                              isLoading={isSubmitting}
                              onClick={() => updateBrainstorms(values, true)}
                              isDisabled={!!errors.brainstorm || !isPlanActive} // Disable if there's an error
                            >
                              Save and Continue
                            </Button>
                          </Flex>
                        </Flex>
                      </Box>
                    </VStack>
                  </Form>
                )}
              </Formik>
            )}
          </Flex>
        </Box>
        <Collapse in={isChatOpen}>
          <Box
            flex="1"
            // position="fixed"
            // bottom="32px"
            // right="32px"
            // overflow="hidden"
            overflowY="auto"
            border="1px solid lightgray"
            borderRadius="md"
            backgroundColor="white"
            padding={2}
            mb={2}
            boxShadow="lg"
            transition="min-width 0.3s ease-in-out"
            zIndex="10"
          >
            <HStack justify="space-between">
              <Box fontSize="lg" fontWeight="bold">
                Chat
              </Box>
              <IconButton
                icon={<CloseIcon />}
                size="sm"
                onClick={toggleChatbox}
                aria-label="Close chat"
              />
            </HStack>
            <Wrapper padding="0px 0px 0px 0px">
              <ChatBox
                messages={messages}
                preMessage={instr_brainstorm}
                essayId={essayId}
                prompt={exerciseData?.prompt}
                grade={exerciseData?.level}
                essayType={exerciseData?.primaryType}
                section={1}
                flexibleChat={updateChatMessage}
              />
            </Wrapper>
          </Box>
        </Collapse>
        {/* Toggle Button (if chat is hidden) */}
        {!isChatOpen && (
          <IconButton
            icon={<FaRocketchat color="#2d8645" />}
            position="fixed" // Keep toggle button fixed
            top={24}
            right={16}
            onClick={toggleChatbox}
            aria-label="Open chat"
            boxSize="36px" // Adjust the button size
            fontSize="32px" // Adjust the icon size
            background="transparent"
            _hover={{ background: 'transparent', color: 'blue.500' }}
          />
        )}
        {/* </HStack> */}
      </Flex>
    </Container>
  );
}

BrainstormForm.propTypes = {
  breadcrumbProg: PropTypes.number,
  setBreadcrumbProg: PropTypes.func,
  nextPath: PropTypes.string,
  returnUrl: PropTypes.string,
  isPlanActive: PropTypes.bool,
};

export default BrainstormForm;
